import {
  Button,
  Checkbox,
  DialogV2,
  InputWrapper,
  Loader,
  TextArea,
  Tooltip,
  Typography,
} from '@recruit-robin/robin-theme/web-components';
import {
  AlertDiamondFilled,
  CalendarArrowUp,
  Check,
  Mail_01,
  Phone,
  PhoneCall_01,
} from '@recruit-robin/robin-theme/web-icons';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Candidate } from 'model/Candidate/Candidate';
import type { UseSelectHookReturn } from '../hooks/useSelect.types';

import { CandidateLabel } from '@/entities/candidate';
import { copyToClipboardWithToast } from '@/shared/lib/clipboard';
import { obfuscateEmail } from '@/shared/lib/string';
import { useGlobalDialog } from '@/stores/global-dialogs/hooks/useGlobalDialog';
import { useUnsavedChanges } from '@/stores/unsaved-changes/hooks/useUnsavedChanges';
import { UnsavedChangesArea } from '@/stores/unsaved-changes/unsaved-changes.types';
import { MobileAppConnectedPill } from 'components/ui/molecules/MobileAppConnectedPill/MobileAppConnectedPill';
import { routes } from 'config/routes';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialogConfig } from 'stores/global-dialogs/hooks/useGlobalDialogConfig';
import { useStyles } from '../CampaignPhoneReachOutDialog.styles';
// import { EmailInput } from './email-input';
import { isCalendarValid, useGetNylasConnectionStatusQuery } from '@/features/nylas-calendar-manage-connection-modal';
import { SelectAtsVacancyModalType } from '@/shared/legacy/contexts/DialogContext/modals/SelectAtsVacancyForCampaignModal/SelectAtsVacancyForCampaignModal.types';
import { useOpenWithAtsVacancyPrompt } from '@/shared/legacy/contexts/DialogContext/modals/SelectAtsVacancyForCampaignModal/useOpenWithAtsVacancyPrompt';
import { useDictionary } from '@/shared/lib/i18n';
import { toast } from '@/shared/lib/toast';
import { useIsGlobalDialogOpen } from '@/stores/global-dialogs/hooks/useIsGlobalDialogOpen';
import { OutcomePill } from './outcome-pill';

export type CallOutcomeScreenProps = {
  note: string;
  setNote: (note: string) => void;
  outcomes: UseSelectHookReturn;
  onSave: (options?: { skipAlternativeChannels?: boolean; newLabels?: string[] }) => void;
  isSaving: boolean;
  candidate: Candidate;
};

export const CallOutcomeScreen = ({ note, setNote, outcomes, onSave, isSaving, candidate }: CallOutcomeScreenProps) => {
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);
  const navigate = useNavigate();
  const unsavedChanges = useUnsavedChanges(UnsavedChangesArea.ReachoutInterviewModal_Content);
  const reachoutInterviewModal = useGlobalDialog(GlobalDialogType.ReachoutInterviewModal);
  const isReachoutInterviewModalOpen = useIsGlobalDialogOpen(GlobalDialogType.ReachoutInterviewModal);
  const campaignPhoneReachOutConfig = useGlobalDialogConfig(GlobalDialogType.CampaignPhoneReachOut);

  const pageContext = usePageContext();
  const { data: currentUser } = useCurrentUserQuery();

  const clickableCandidateName = [PageContext.Shared, PageContext.Candidate].includes(pageContext);
  const candidateEmail = candidate.not_obfuscated_email;

  useEffect(() => {
    if (isReachoutInterviewModalOpen) {
      return () => {
        reachoutInterviewModal.close();
      };
    }
  }, [isReachoutInterviewModalOpen]);

  const handleClickCandidateName = () => {
    if (!clickableCandidateName) return;

    unsavedChanges.withNavigationGuard(() => {
      navigate({
        pathname: routes.candidatePage({
          candidateId: candidate.es_person_id,
          campaignId: candidate.campaignId,
          userId: currentUser?.id,
        }),
      });
      reachoutInterviewModal.close();
    });
  };

  const handleToggleDontShowThisNextTime = () => {
    campaignPhoneReachOutConfig.set({ skipFollowUp: !campaignPhoneReachOutConfig.config.skipFollowUp });
  };

  const handleSave = () => {
    unsavedChanges.withNavigationGuard(() => {
      onSave();
      reachoutInterviewModal.close();
    });
  };

  return (
    <>
      <DialogV2.Header customCss={classes.contentHeader}>
        <div css={classes.contentHeaderTitle}>
          <Phone size={24} />
          <span>
            <Typography variant={(v) => v.heading[5]}>
              {dictionary.callingWith(
                <span
                  css={[clickableCandidateName && classes.candidateNameSharedPage]}
                  onClick={handleClickCandidateName}>
                  {candidate.name}
                </span>,
              )}
            </Typography>
          </span>
          <MobileAppConnectedPill candidate={candidate} />
        </div>
        <div css={classes.contentHeaderSubtitle}>
          <a css={classes.pill} href={`tel:${candidate.not_obfuscated_phone}`}>
            <PhoneCall_01 size={16} color={(c) => c.neutral.variant[70]} />
            <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
              {candidate.not_obfuscated_phone}
            </Typography>
          </a>

          {candidateEmail ? (
            <span css={classes.pill} onClick={() => copyToClipboardWithToast(candidateEmail)}>
              <Mail_01 size={16} color={(c) => c.neutral.variant[70]} />
              <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
                {obfuscateEmail(candidateEmail, 3)}
              </Typography>
            </span>
          ) : (
            <span css={[classes.pill, classes.disabledPill]}>
              <Mail_01 size={16} color={(c) => c.neutral.variant[40]} />
              <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[40]}>
                {dictionary.emailNotAvaialbe}
              </Typography>
            </span>
          )}
        </div>

        {/* {!candidateEmail && <EmailInput candidate={candidate} />} */}
      </DialogV2.Header>

      <DialogV2.Body customCss={classes.contentBody}>
        <TextArea
          customCss={classes.contentBodyTextArea}
          placeholder={dictionary.typeYourNote}
          defaultValue={note}
          onChange={(e) => setNote(e.target.value)}
          name="campaign-phone-reachout-dialog-note"
        />
      </DialogV2.Body>

      <DialogV2.Footer customCss={classes.contentFooter}>
        <InputWrapper label={dictionary.selectCallOutcome} requiredIndicator contained>
          <OutcomePill.Group selectedValue={outcomes.selected} onSelect={outcomes.toggle}>
            <OutcomePill label={dictionary.notResponding} value={CandidateLabel.NotResponding} />
            <OutcomePill label={dictionary.notInterested} value={CandidateLabel.NotInterested} />
            <OutcomePill label={dictionary.moreInfoSent} value={CandidateLabel.MoreInfoSent} />
            <OutcomePill label={dictionary.interviewScheduled} value={CandidateLabel.InterviewScheduled} />
            <OutcomePill label={dictionary.leftVoicemail} value={CandidateLabel.LeftVoicemail} />
            <OutcomePill.Dropdown initialOptions={outcomes.initialValue} />
          </OutcomePill.Group>

          <Typography variant={(v) => v.supporting.helper} customCss={classes.selectHelper}>
            <AlertDiamondFilled size={18} />
            {dictionary.theLabelSelectionIsRequired}
          </Typography>
        </InputWrapper>

        {pageContext !== PageContext.CentralInbox && (
          <Checkbox
            label={
              <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[70]}>
                {dictionary.alwaysShowAlternativeChannels}
              </Typography>
            }
            checked={!campaignPhoneReachOutConfig.config.skipFollowUp}
            onClick={handleToggleDontShowThisNextTime}
          />
        )}

        <div css={classes.saveButtonContainer}>
          <InterviewSchedulerButton candidate={candidate} outcomes={outcomes} onSave={onSave} />
          <Button
            label={dictionary.save}
            onClick={handleSave}
            style="filled"
            type="success"
            startIcon={
              isSaving ? <Loader size={16} variant="ring-4" color={(c) => c.secondary.emerald} /> : <Check size={16} />
            }
            disabled={!isRequiredOutcomeSelected(outcomes)}
          />
        </div>
      </DialogV2.Footer>
    </>
  );
};

const isRequiredOutcomeSelected = (outcomes: UseSelectHookReturn) => {
  if (
    [
      CandidateLabel.NotResponding,
      CandidateLabel.NotInterested,
      CandidateLabel.MoreInfoSent,
      CandidateLabel.InterviewScheduled,
      CandidateLabel.LeftVoicemail,
    ].some((outcome) => outcomes.selected.includes(outcome))
  ) {
    return true;
  }

  return false;
};

const InterviewSchedulerButton = ({
  candidate,
  outcomes,
  onSave,
}: {
  candidate: Candidate;
  outcomes: UseSelectHookReturn;
  onSave: (options?: { skipAlternativeChannels?: boolean; newLabels?: string[] }) => void;
}) => {
  const dictionary = useDictionary();
  const { data: nylasConnectionStatus } = useGetNylasConnectionStatusQuery();
  const isCalendarValidConnection = isCalendarValid(nylasConnectionStatus);
  const reachoutInterviewModal = useGlobalDialog(GlobalDialogType.ReachoutInterviewModal);
  const nylasCalendarConnectionModal = useGlobalDialog(GlobalDialogType.NylasCalendarManageConnectionModal);

  const openReachoutInterviewModal = () => {
    reachoutInterviewModal.open({
      payload: {
        esPersonId: candidate.es_person_id,
        onInterviewBooked: () => {
          // Do not create a custom log here. The backend creates the CALENDAR_INTERVIEW_INVITATION_ACCEPTED
          // entry when Nylas sends booking.created (webhook → Google Task). Locally the webhook does not run,
          // so candidate history will not show the booking—that is expected; do not "fix" it by re-adding a log.
          reachoutInterviewModal.close();

          const newLabels = outcomes.selected.filter((label) => label !== CandidateLabel.InterviewScheduled);
          newLabels.push(CandidateLabel.InterviewScheduled);

          toast.success(dictionary.interviewBookedToastMessage(candidate.name), {
            title: dictionary.interviewBookedToastTitle,
          });

          onSave({ skipAlternativeChannels: true, newLabels });
        },
      },
    });
  };

  const openWithAtsPrompt = useOpenWithAtsVacancyPrompt({
    openAction: openReachoutInterviewModal,
    candidateFirstName: candidate.firstName,
    type: SelectAtsVacancyModalType.Scheduling,
  });

  const handleClick = () => {
    if (!isCalendarValidConnection) {
      nylasCalendarConnectionModal.open({
        payload: {
          onFinish: () => {
            nylasCalendarConnectionModal.close();
            openWithAtsPrompt();
          },
          onCancel: () => {
            nylasCalendarConnectionModal.close();
          },
        },
      });
      return;
    }

    openWithAtsPrompt();
  };

  return (
    <Tooltip
      label={
        isCalendarValidConnection
          ? dictionary.pleaseGrantAccessToYourCalendar
          : dictionary.pleaseConnectYourEmailToUseInterviewScheduler
      }
      zIndex={1000}
      offset={{ mainAxis: 8 }}>
      <Button
        label={dictionary.scheduleInterviewNow}
        onClick={handleClick}
        style="tonal"
        type="highlight"
        startIcon={<CalendarArrowUp size={16} />}
      />
    </Tooltip>
  );
};
